import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import WrappedHeader from './WrappedHeader/WrappedHeader'
import type { SlideConfig } from '../types/wrapped'
import {
  WrappedStoryActions,
  useWrappedStorySelector,
  useWrappedStoryDispatch,
} from 'contexts/Wrapped'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import WrappedAudioPlayer from './WrappedAudioPlayer'
import useStateWithRef from 'hooks/useStateWithRef'

// Slides
import IntroSlide from './Slides/IntroSlide'
import TravelPersonalitySlide from './Slides/TravelPersonalitySlide'
import DestinationsSlide from './Slides/DestinationsSlide'
import NightsSpentSlide from './Slides/NightsSpentSlide'
import DistanceSlide from './Slides/DistanceSlide'
import TopDestinationsQuizSlide from './Slides/TopDestinationsQuizSlide'
import OfferViewsSlide from './Slides/OfferViewsSlide'
import TopOfferSlide from './Slides/TopOfferSlide'
import SavingsSlide from './Slides/SavingsSlide'
import LoyaltySlide from './Slides/LoyaltySlide'
import SummarySlide from './Slides/SummarySlide'
import PredictionSlide from './Slides/PredictionSlide'

const SLIDE_COMPONENT_MAP: Record<SlideConfig['id'], React.ComponentType> = {
  'intro': IntroSlide,
  'personality': TravelPersonalitySlide,
  'destinations': DestinationsSlide,
  'nights': NightsSpentSlide,
  'distance': DistanceSlide,
  'top-destinations-quiz': TopDestinationsQuizSlide,
  'offer-views': OfferViewsSlide,
  'top-offer': TopOfferSlide,
  'savings': SavingsSlide,
  'loyalty': LoyaltySlide,
  'summary': SummarySlide,
  'prediction': PredictionSlide,
}

const StyledPane = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  background-color: ${props => props.theme.palette.neutral.default.one};
  overflow: hidden;

  &:focus {
    outline: none;
  }

  .is-full-page & {
    max-width: none;
  }
`

const SlideWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const SlideContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  touch-action: pan-y;
`

const SlideLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
  content-visibility: hidden;
  contain-intrinsic-size: 100% 100%;
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
  backface-visibility: hidden;
  transform: translateZ(0);

  &.active {
    opacity: 1;
    pointer-events: auto;
    overflow-y: auto;
    z-index: 1;
    content-visibility: visible;
    transform: translateX(0);
  }

  &.prev {
    transform: translateX(-100%);
  }

  &.next {
    transform: translateX(100%);
  }

  &.dragging {
    transition: none;
    opacity: 1;
    content-visibility: visible;
    z-index: 1;

    &.active {
      z-index: 2;
    }
  }
`

const NavigationArrow = styled(IconButton)`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  transition: opacity 0.2s;
  opacity: 0;
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }

  &.left {
    left: ${rem(12)};
  }

  &.right {
    right: ${rem(12)};
  }

  &.visible {
    opacity: 0.6;
    pointer-events: auto;

    &:hover, &:focus {
      opacity: 0.9;
    }
  }
`

const TAP_ZONE_WIDTH_FRACTION = 0.25
const SWIPE_THRESHOLD = 50

interface Props {
  mode: 'modal' | 'fullPage'
  onClose?: () => void
}

function WrappedStoryContent({ mode, onClose }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const dispatch = useWrappedStoryDispatch()
  const isMobile = useIsMobileDevice()

  const currentIndex = useWrappedStorySelector(state => state.currentIndex)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const slides = useWrappedStorySelector(state => state.slides)
  const currentSlide = useWrappedStorySelector(state => state.currentSlide)
  const phase = useWrappedStorySelector(state => state.phase)

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Track visited slides to keep their videos cached (prevents reload when returning to a slide)
  // Only relevant for slides with video backgrounds
  const visitedSlidesRef = useRef<Set<number>>(new Set([0]))

  const slideInfoRef = useRef({ currentIndex, slides })
  slideInfoRef.current = { currentIndex, slides }

  const slidesLengthRef = useRef(slides.length)
  slidesLengthRef.current = slides.length

  // Mark current slide as visited to keep video cached
  useEffect(() => {
    visitedSlidesRef.current.add(currentIndex)
  }, [currentIndex])

  const hasVideo = !!currentSlide?.video
  // Slides with fallbackImageId use image-first approach and handle isVideoReady themselves
  const usesImageFirstApproach = hasVideo && !!currentSlide?.fallbackImageId

  useEffect(() => {
    // Skip dispatch for image-first slides - they manage isVideoReady via useVideoBackgroundWithFallback
    if (usesImageFirstApproach) {
      return
    }

    if (hasVideo) {
      dispatch({ type: WrappedStoryActions.setVideoReady, isVideoReady: false })
    } else {
      dispatch({ type: WrappedStoryActions.setVideoReady, isVideoReady: true })
    }
  }, [currentIndex, hasVideo, usesImageFirstApproach, dispatch])

  const handleClose = useCallback(() => {
    if (mode === 'fullPage' && onClose) {
      onClose()
    } else {
      modalElement?.resolve()
    }
  }, [modalElement, mode, onClose])

  const goToNextSlide = useCallback(() => {
    const { currentIndex } = slideInfoRef.current
    const slidesLength = slidesLengthRef.current
    if (currentIndex < slidesLength - 1) {
      dispatch({ type: WrappedStoryActions.setCurrentSlide, index: currentIndex + 1 })
    }
  }, [dispatch])

  const goToPrevSlide = useCallback(() => {
    const { currentIndex } = slideInfoRef.current
    dispatch({ type: WrappedStoryActions.setCurrentSlide, index: Math.max(0, currentIndex - 1) })
  }, [dispatch])

  const goToSlide = useCallback((index: number) => {
    dispatch({ type: WrappedStoryActions.setCurrentSlide, index })
  }, [dispatch])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { currentIndex, slides } = slideInfoRef.current
    const currentSlide = slides[currentIndex]
    const scrollTop = e.currentTarget.scrollTop
    dispatch({
      type: WrappedStoryActions.setScrolled,
      isScrolled: scrollTop > 0 && !currentSlide?.imageId && !currentSlide?.video,
    })
  }, [dispatch])

  const [hoverZone, setHoverZone] = useStateWithRef<'left' | 'right' | null>(null)
  const [isDragging, setIsDragging] = useStateWithRef(false)

  // Store refs to slide elements for direct DOM manipulation during swipe
  const slideRefsMap = useRef<Map<string, HTMLDivElement>>(new Map())
  const touchStateRef = useRef<{
    startX: number
    startY: number
    isHorizontal: boolean | null
    containerWidth: number
  } | null>(null)

  // Get slide element ref callback
  const getSlideRef = useCallback((slideId: string) => (el: HTMLDivElement | null) => {
    if (el) {
      slideRefsMap.current.set(slideId, el)
    } else {
      slideRefsMap.current.delete(slideId)
    }
  }, [])

  // Update slide transforms directly for performance (no React state updates during drag)
  const updateSlideTransforms = useCallback((dragOffset: number) => {
    const { currentIndex, slides } = slideInfoRef.current
    const containerWidth = touchStateRef.current?.containerWidth ?? 1

    slides.forEach((slide, index) => {
      const el = slideRefsMap.current.get(slide.id)
      if (!el) return

      const isActive = index === currentIndex
      const isPrev = index === currentIndex - 1
      const isNext = index === currentIndex + 1

      if (!isActive && !isPrev && !isNext) return

      const dragPercent = (dragOffset / containerWidth) * 100
      let translateX: number

      if (isActive) {
        translateX = dragPercent
      } else if (isPrev) {
        translateX = -100 + dragPercent
      } else {
        translateX = 100 + dragPercent
      }

      el.style.transform = `translateX(${translateX}%)`
    })
  }, [])

  // Reset slide transforms to CSS-controlled state
  const resetSlideTransforms = useCallback(() => {
    slideRefsMap.current.forEach((el) => {
      el.style.transform = ''
    })
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0]
    const containerWidth = e.currentTarget.getBoundingClientRect().width

    touchStateRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      isHorizontal: null,
      containerWidth,
    }
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const state = touchStateRef.current
    if (!state) return

    const touch = e.touches[0]
    const deltaX = touch.clientX - state.startX
    const deltaY = touch.clientY - state.startY

    // Determine swipe direction on first significant movement
    if (state.isHorizontal === null && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      state.isHorizontal = Math.abs(deltaX) > Math.abs(deltaY)

      // Start dragging state only when we confirm horizontal swipe
      if (state.isHorizontal) {
        setIsDragging(true)
      }
    }

    // Only track horizontal swipes
    if (state.isHorizontal) {
      const { currentIndex } = slideInfoRef.current
      const slidesLength = slidesLengthRef.current
      const isAtStart = currentIndex === 0 && deltaX > 0
      const isAtEnd = currentIndex === slidesLength - 1 && deltaX < 0

      // Apply resistance at boundaries
      const effectiveOffset = (isAtStart || isAtEnd) ? deltaX * 0.3 : deltaX
      updateSlideTransforms(effectiveOffset)
    }
  }, [setIsDragging, updateSlideTransforms])

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const state = touchStateRef.current
    if (!state) return

    const touch = e.changedTouches[0]
    const deltaX = touch.clientX - state.startX

    // Reset transforms before navigation (CSS transition will take over)
    resetSlideTransforms()

    // Navigate if swipe was significant and horizontal
    if (state.isHorizontal && Math.abs(deltaX) > SWIPE_THRESHOLD) {
      if (deltaX > 0) {
        goToPrevSlide()
      } else {
        goToNextSlide()
      }
    }

    touchStateRef.current = null
    setIsDragging(false)
  }, [goToPrevSlide, goToNextSlide, resetSlideTransforms, setIsDragging])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const width = rect.width

    if (x < width * TAP_ZONE_WIDTH_FRACTION) {
      setHoverZone('left')
    } else if (x > width * (1 - TAP_ZONE_WIDTH_FRACTION)) {
      setHoverZone('right')
    } else {
      setHoverZone(null)
    }
  }, [setHoverZone])

  const handleMouseLeave = useCallback(() => {
    setHoverZone(null)
  }, [setHoverZone])

  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-interactive-zone]')) {
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const width = rect.width

    if (x < width * TAP_ZONE_WIDTH_FRACTION) {
      goToPrevSlide()
    } else if (x > width * (1 - TAP_ZONE_WIDTH_FRACTION)) {
      goToNextSlide()
    }
  }, [goToPrevSlide, goToNextSlide])

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowRight':
        goToNextSlide()
        break
      case 'ArrowLeft':
        goToPrevSlide()
        break
      case ' ':
        event.preventDefault()
        dispatch({ type: WrappedStoryActions.togglePause, isPaused: undefined })
        break
      case 'Escape':
        handleClose()
        break
    }
  }, [goToNextSlide, goToPrevSlide, handleClose, dispatch])

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    if (isPaused || phase === 'question' || currentSlide?.duration === 0 || !isVideoReady || currentSlide?.waitForClick) {
      return
    }

    timerRef.current = setTimeout(() => {
      goToNextSlide()
    }, currentSlide?.duration ?? 5000)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [currentIndex, isPaused, currentSlide, goToNextSlide, isVideoReady, phase])

  const slideElements = useMemo(() => {
    return slides.map(slide => {
      const SlideComponent = SLIDE_COMPONENT_MAP[slide.id]
      return SlideComponent ? <SlideComponent key={slide.id} /> : null
    })
  }, [slides])

  useEffect(() => {
    containerRef.current?.focus()
  }, [])

  // Need slides to render (can come from user data or totals-only mode)
  if (slides.length === 0) return null

  return (
    <StyledPane
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label={intl.formatMessage({ id: '58hdzqAMNT', defaultMessage: 'Year in Review story', description: 'Aria-label for Wrapped story content region in the wrapped summary' })}
    >
      <WrappedAudioPlayer />
      <WrappedHeader
        onClose={handleClose}
        onGoToSlide={goToSlide}
      />
      <SlideWrapper>
        <SlideContent
          onClick={handleContentClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {slides.map((slide, index) => {
            const isNearby = Math.abs(index - currentIndex) <= 1
            // Keep slides mounted if: nearby, or visited with video (to cache video)
            const hasVideoBackground = !!slide.video
            const wasVisited = visitedSlidesRef.current.has(index)
            const shouldKeepMounted = isNearby || (hasVideoBackground && wasVisited)
            if (isMobile && !shouldKeepMounted) return null

            const isActive = index === currentIndex
            const isPrev = index === currentIndex - 1
            const isNext = index === currentIndex + 1
            const isAdjacent = isPrev || isNext

            return (
              <SlideLayer
                key={slide.id}
                ref={getSlideRef(slide.id)}
                className={clsx({
                  active: isActive && !isDragging,
                  prev: isPrev && !isDragging,
                  next: isNext && !isDragging,
                  dragging: isDragging && (isActive || isAdjacent),
                })}
                onScroll={isActive ? handleScroll : undefined}
              >
                {slideElements[index]}
              </SlideLayer>
            )
          })}
          {currentIndex > 0 && (
            <NavigationArrow
              className={clsx('left', { visible: hoverZone === 'left' })}
              onClick={goToPrevSlide}
              kind="primary"
              variant="ghost"
              shape="circle"
              size="large"
              aria-label={intl.formatMessage({ id: 'sOjwD6A1WM', defaultMessage: 'Previous slide', description: 'Accessibility aria-label for previous slide navigation button' })}
            >
              <LineAngleLeftIcon />
            </NavigationArrow>
          )}
          {currentIndex < slides.length - 1 && (
            <NavigationArrow
              className={clsx('right', { visible: hoverZone === 'right' })}
              onClick={goToNextSlide}
              kind="primary"
              variant="ghost"
              shape="circle"
              size="large"
              aria-label={intl.formatMessage({ id: 'SGfmqfwA2F', defaultMessage: 'Next slide', description: 'Aria-label for next slide navigation button in the wrapped summary' })}
            >
              <LineAngleRightIcon />
            </NavigationArrow>
          )}
        </SlideContent>
      </SlideWrapper>
    </StyledPane>
  )
}

export default WrappedStoryContent
